import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Calendar } from 'lucide-react';
import MouseFollowEffect from './MouseFollowEffect';

const services = [
  { id: 'email-marketing', text: 'Email Marketing', slug: 'email-marketing' },
  { id: 'content-creation', text: 'Content Creation', slug: 'content-creation' },
  { id: 'brand-design', text: 'Brand Design', slug: 'brand-design' },
  { id: 'analytics-reporting', text: 'Analytics & Reporting', slug: 'analytics-reporting' },
  { id: 'creative-strategy', text: 'Creative Strategy', slug: 'creative-strategy' },
  { id: 'social-strategy', text: 'Social Strategy', slug: 'social-strategy' }
];

const navigationLinks = [
  { to: '/', text: 'Home' },
  { to: '/services', text: 'Services' },
  { to: '/work', text: 'Portfolio' },
  { to: '/blog', text: 'Blog' },
  { to: '/about', text: 'About' },
  { to: '/schedule', text: 'Schedule Call' },
  { to: '/contact', text: 'Contact' }
];

export default function Footer() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const handleServiceClick = (serviceId: string) => {
    navigate('/contact', { state: { selectedService: serviceId } });
  };

  return (
    <footer className="bg-black/90 border-t border-gray-800 py-12">
      <MouseFollowEffect className="bg-transparent">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <NavLink to="/" className="flex items-center space-x-2 text-golden mb-4 interactive" aria-label="DS Marketing Home">
                <span className="text-xl font-bold float-medium">DS Marketing</span>
              </NavLink>
              <p className="text-gray-300">
                Transforming brands through creative excellence and strategic innovation.
              </p>
              <div className="mt-4">
                <NavLink
                  to="/schedule"
                  className="inline-flex items-center text-golden hover:text-white transition-colors"
                  aria-label="Book a free strategy call"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  <span className="font-medium">Book a free strategy call</span>
                </NavLink>
              </div>
            </div>

            {/* Menu */}
            <div>
              <h3 className="text-white font-semibold mb-4">Menu</h3>
              <ul className="space-y-2">
                {navigationLinks.map((link) => (
                  <li key={link.text}>
                    <NavLink
                      to={link.to}
                      className="text-gray-300 hover:text-golden transition-all duration-300 transform hover:translate-x-2 inline-block interactive"
                    >
                      {link.text}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-white font-semibold mb-4">Services</h3>
              <ul className="space-y-2">
                {services.map((service) => (
                  <li key={service.id}>
                    <button
                      onClick={() => handleServiceClick(service.id)}
                      className="text-gray-300 transition-all duration-300 transform hover:translate-x-2 hover:text-golden text-left interactive"
                    >
                      {service.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-white font-semibold mb-4">Contact Info</h3>
              <ul className="space-y-4">
                <li className="flex items-center text-gray-300 hover:text-golden transition-colors duration-300 interactive">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+33 7 85 32 24 64</span>
                </li>
                <li className="flex items-center text-gray-300 hover:text-golden transition-colors duration-300 interactive">
                  <Mail className="w-5 h-5 mr-2" />
                  <span>contact@dsmarketing.fr</span>
                </li>
                <li className="flex items-center text-gray-300 hover:text-golden transition-colors duration-300 interactive">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>20 Avenue de Bardanac, Bordeaux</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center pt-8 border-t border-gray-800">
            <p className="text-gray-300 font-medium">
              Copyright © {currentYear} DS Marketing. All rights reserved.
            </p>
          </div>
        </div>
      </MouseFollowEffect>
    </footer>
  );
}